import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tag, RotateCcw } from 'lucide-react';
import type { Product } from '../../types';
import { FeaturedProductCard } from '../../components/product/FeaturedProductCard';
import { SearchFilterBar } from '../../components/search/SearchFilterBar';
import { useProductFacets, useOrigine } from '../../hooks/useProductFacets';
import { matchesTipologia } from '../../utils/tipologiaMatch';
import { productService } from '../../services/productService';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

/**
 * Pagina Outlet — articoli con flag NTS ``ar_inesaur='S'`` (in esaurimento).
 *
 * Filtri (stessa barra orizzontale condivisa con la ricerca prodotti):
 * Marca, Tipologia, Origine (Originali/Compatibili o Solo compatibili),
 * Cod. DTK/OEM, Modello.
 * Le opzioni marca/tipologia/origine vengono caricate via
 * ``/api/products/facets``; il filtro Modello risolve gli SKU compatibili
 * via ``/api/abbinatore/modello/{id}/skus`` (stesso endpoint della ricerca
 * per modello). Tutto è applicato lato client sui prodotti già scaricati
 * (set piccolo, in esaurimento ≪ 100 SKU) — nessun bottone "Cerca", i filtri
 * sono live.
 */
function fmtPrice(n: number): string {
  return n.toFixed(2).replace('.', ',');
}

export default function OutletPage() {
  const { isLoading: authLoading } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const [marca, setMarca] = useState('');
  const [tipologia, setTipologia] = useState('');
  const [codice, setCodice] = useState('');
  const [modelloId, setModelloId] = useState('');
  const [modelloLabel, setModelloLabel] = useState('');
  const [modelloSkus, setModelloSkus] = useState<Set<string> | null>(null);

  const { facets, loadingFacets, origineOptions, tipologiaOptions, originiSelezione } =
    useProductFacets(marca, tipologia, { onTipologiaNonPiuValida: () => setTipologia('') });
  // Senza originali nella selezione passa da solo a "Solo compatibili".
  const { origine, scegliOrigine } = useOrigine(originiSelezione);

  // Risolve gli SKU compatibili con il modello selezionato (filtro Modello).
  useEffect(() => {
    if (!modelloId) { setModelloSkus(null); return; }
    let cancelled = false;
    api.get<{ skus: string[] }>(`/api/abbinatore/modello/${modelloId}/skus`)
      .then(res => { if (!cancelled) setModelloSkus(new Set(res.data.skus || [])); })
      .catch(() => { if (!cancelled) setModelloSkus(new Set()); });
    return () => { cancelled = true; };
  }, [modelloId]);

  // Carica i prodotti outlet (filtro server-side per inEsaurimento) — attende
  // il restore della sessione (bug 26/02/2026, stesso fix di HomePage.tsx)
  // per evitare che la prima chiamata parta senza header Authorization.
  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    productService
      .list({ inEsaurimento: true, publicOnly: true, limit: 96 })
      .then(res => { if (!cancelled) setProducts(res.items); })
      .catch(() => { if (!cancelled) setProducts([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [authLoading]);

  // Filtro lato client sui prodotti già scaricati
  const filtered = useMemo(() => {
    return products.filter(p => {
      if (marca && (p.brand || '').toLowerCase() !== marca.toLowerCase()) return false;
      // La ricerca per codice è un lookup puntuale: ignora il filtro
      // Tipologia (un codice può appartenere a una categoria diversa da
      // quella selezionata, non deve risultare "non trovato" per questo).
      // Filtro sul gruppo/sottogruppo merceologico reale (bug 25/08/2026:
      // confrontava `p.category`, lo slug legacy a 5 valori, che non
      // coincide quasi mai col gruppo/sottogruppo selezionato nel filtro).
      if (!codice.trim() && !matchesTipologia(p, tipologia)) return false;
      if (origine && (p.type || '').toLowerCase() !== origine) return false;
      if (modelloSkus && !modelloSkus.has(p.sku)) return false;
      if (codice.trim()) {
        const q = codice.trim().toLowerCase();
        const matchesOem = (p.oemCodes || []).some(c => c.toLowerCase().includes(q));
        if (!p.sku.toLowerCase().includes(q) && !p.dtkCodice?.toLowerCase().includes(q) && !matchesOem) return false;
      }
      return true;
    });
  }, [products, marca, tipologia, origine, codice, modelloSkus]);

  const isDirty = !!(marca || tipologia || origine || codice || modelloId);

  const reset = () => {
    setMarca('');
    setTipologia('');
    scegliOrigine('');
    setCodice('');
    setModelloId('');
    setModelloLabel('');
  };

  return (
    <div data-testid="outlet-page" className="min-h-screen bg-secondary/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Tag className="w-6 h-6 text-red-500" />
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Outlet</h1>
          </div>
          <p className="text-muted-foreground text-sm sm:text-base">
            Articoli in esaurimento, disponibilità limitata. Approfittane prima che terminino.
          </p>
        </div>

        {/* Ricerca avanzata orizzontale — stessa barra della ricerca prodotti */}
        <SearchFilterBar
          marca={marca} onMarcaChange={v => { setMarca(v); setModelloId(''); setModelloLabel(''); }}
          tipologia={tipologia} onTipologiaChange={setTipologia}
          origine={origine} onOrigineChange={scegliOrigine}
          facets={facets} loadingFacets={loadingFacets} origineOptions={origineOptions} tipologiaOptions={tipologiaOptions}
          modello={{ id: modelloId, label: modelloLabel, onChange: (id, label) => { setModelloId(id); setModelloLabel(label); } }}
          codice={{ value: codice, onChange: setCodice }}
          onReset={reset}
          isDirty={isDirty}
          resultCount={filtered.length}
          testidPrefix="outlet-filter"
        />

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="outlet-loading">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border h-[360px] animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="outlet-empty">
            <Tag className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Nessun articolo in esaurimento al momento.</p>
            <p className="text-xs mt-1 opacity-70">
              Torna a trovarci: gli articoli vengono marcati "in esaurimento" dal gestionale (NTS, campo <code className="font-mono">ar_inesaur</code>).
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="outlet-empty-filtered">
            <Tag className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Nessun articolo Outlet corrisponde ai filtri.</p>
            <button
              type="button"
              onClick={reset}
              className="mt-3 text-xs text-[#2B5EA7] hover:underline inline-flex items-center gap-1"
            >
              <RotateCcw className="w-3 h-3" /> Azzera filtri
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="outlet-grid">
            {filtered.map(p => {
              const promo = p.promo;
              const priceCurrent = fmtPrice(promo?.priceOverride ?? p.listino10 ?? 0);
              const priceOld = promo?.priceOverride && p.listino10 && p.listino10 > promo.priceOverride
                ? fmtPrice(p.listino10)
                : undefined;
              const stock = (p.stock as number | undefined) ?? 0;
              return (
                <FeaturedProductCard
                  key={p.sku}
                  product={p}
                  tag="OUTLET"
                  priceCurrent={priceCurrent}
                  priceOld={priceOld}
                  stock={stock}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
